import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, Output, signal, viewChild } from '@angular/core';

import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { SharedModule } from 'src/app/features/shared/shared.module';

// In-pagina camera (getUserMedia) als vervanging van de <input capture>-knop: die opent op mobiel
// de externe camera-app, waardoor Chrome onder geheugendruk de tab weggooit en de pagina herlaadt —
// de gemaakte foto gaat dan verloren vóór de upload. Door de camera binnen de pagina te draaien
// blijft Chrome op de voorgrond en wordt het frame direct als JPEG-File geëmit naar de fotolijst.
@Component({
    selector: 'app-camera-capture',
    templateUrl: './camera-capture.component.html',
    styleUrls: ['./camera-capture.component.scss'],
    standalone: true,
    imports: [
        ButtonActionComponent,
        SharedModule,
    ],
})
export class CameraCaptureComponent implements AfterViewInit, OnDestroy {

    @Output() captured = new EventEmitter<File>();
    @Output() closed = new EventEmitter<void>();

    busy$$ = signal(false);
    error$$ = signal<string | null>(null);
    ready$$ = signal(false);

    private __facingMode: 'environment' | 'user' = 'environment';
    private __stream: MediaStream | null = null;

    private readonly __video$$ = viewChild<ElementRef<HTMLVideoElement>>('video');
    private readonly __overlay$$ = viewChild<ElementRef<HTMLDivElement>>('overlay');

    ngAfterViewInit(): void {
        // De fullscreen-overlay naar <body> verplaatsen: in de tab-/lijstpagina zit dit component in
        // een lager stacking-context (show/hide-transform op een voorouder), waardoor de page-footer
        // — met name de onzichtbare veeg-strip erboven — over de onderkant van de overlay valt en de
        // klikken op de camera-knoppen (de icoon-rij) opslokt. Vanuit <body> staat de overlay echt
        // bovenaan. De component-scoping (attribuut-selectors) blijft behouden na de verplaatsing.
        const __overlay = this.__overlay$$()?.nativeElement;
        if (__overlay) document.body.appendChild(__overlay);

        void this.__start();
    }

    // Foto maken: het huidige videoframe naar een canvas tekenen en als JPEG-File emitten. De
    // bestaande upload-flow (resize + image-save) verwerkt het bestand verder.
    async emit_capture(): Promise<void> {
        const __video = this.__video$$()?.nativeElement;
        if (!__video || !this.ready$$() || this.busy$$()) return;

        this.busy$$.set(true);
        try {
            const __file = await this.__grabFrame(__video);
            if (__file) this.captured.emit(__file);
        } finally {
            this.busy$$.set(false);
        }
    }

    emit_cancel(): void {
        this.closed.emit();
    }

    // Wissel tussen achter- en voorcamera en herstart de stream.
    async emit_switchCamera(): Promise<void> {
        if (this.busy$$()) return;
        this.__facingMode = this.__facingMode === 'environment' ? 'user' : 'environment';
        await this.__start();
    }

    private __errorText(error: unknown): string {
        const __name = (error as DOMException)?.name;
        if (__name === 'NotAllowedError' || __name === 'SecurityError') return 'camera permission denied';
        if (__name === 'NotFoundError') return 'no camera found';
        return 'camera error';
    }

    // Teken het frame op ware grootte naar een canvas en lever een JPEG-File.
    private __grabFrame(video: HTMLVideoElement): Promise<File | null> {
        const __w = video.videoWidth;
        const __h = video.videoHeight;
        if (!__w || !__h) return Promise.resolve(null);

        const __canvas = document.createElement('canvas');
        __canvas.width = __w;
        __canvas.height = __h;
        __canvas.getContext('2d')!.drawImage(video, 0, 0, __w, __h);

        return new Promise<File | null>(resolve => {
            __canvas.toBlob(
                blob => resolve(blob ? new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' }) : null),
                'image/jpeg',
                0.92,
            );
        });
    }

    private async __start(): Promise<void> {
        this.error$$.set(null);
        this.ready$$.set(false);
        this.__stop();

        if (!navigator.mediaDevices?.getUserMedia) {
            this.error$$.set('camera not supported');
            return;
        }

        try {
            this.__stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: this.__facingMode } },
                audio: false,
            });
            const __video = this.__video$$()?.nativeElement;
            if (__video) {
                __video.srcObject = this.__stream;
                await __video.play();
            }
            this.ready$$.set(true);
        } catch (error) {
            this.error$$.set(this.__errorText(error));
        }
    }

    // Stop alle tracks: zonder dit blijft de cameraled/het toestel bezet na sluiten.
    private __stop(): void {
        this.__stream?.getTracks().forEach(track => track.stop());
        this.__stream = null;
    }

    ngOnDestroy(): void {
        this.__stop();
        // De naar <body> verplaatste overlay zelf opruimen (Angular ruimt alleen de oorspronkelijke
        // host-positie op, niet het verplaatste element).
        this.__overlay$$()?.nativeElement?.remove();
    }
}
